import * as React from 'react';
import { Form, Segment } from 'semantic-ui-react';

import Panel from '../../../../../Panel';

import { fechaActual } from '../../../../../../../comun/utils/fechaActual';

import { Contexto } from 'src/contexto';
import DatosMatriculaIngles, {
    Datos as InglesDatos,
    inicial as inglesInicial,
} from './componentes/DatosMatriculaIngles';
import DatosMatriculaPreu, {
    Datos as PreuniversitarioDatos,
    inicial as preuniversitarioInicial,
} from './componentes/DatosMatriculaPreu';

const fakeDB = {
    fechas: {
        inicioClases: { preu: '2021-04-05', ingles: '2021-03-15' },
        finClases: { preu: '2021-11-26', ingles: '2021-12-17' },
    },
    areas: [
        { id: 'preu', texto: 'Preuniversitario' },
        { id: 'ingles', texto: 'Inglés' },
        { id: 'aleman', texto: 'Alemán' },
        { id: 'japones', texto: 'Japonés' },
    ],
    planesPorArea: {
        preu: [
            { id: 'anual', texto: 'Anual' },
            { id: 'intensivo', texto: 'Intensivo' },
            { id: 'intensivo-vespertino', texto: 'Intensivo vespertino' },
        ],
        ingles: [
            { id: 'anual', texto: 'Anual' },
            { id: 'intensivo', texto: 'Intensivo' },
        ],
        aleman: [
            { id: 'anual', texto: 'Anual' },
            { id: 'intensivo', texto: 'Intensivo' },
        ],
        japones: [
            { id: 'anual', texto: 'Anual' },
            { id: 'intensivo', texto: 'Intensivo' },
        ],
    },
};

export interface Datos {
    fechaContrato: string;
    plan: 'anual' | 'intensivo' | 'intensivo-vespertino';
    fechaInicioClases: string;
    fechaTerminoClases: string;
    area: 'preu' | 'ingles' | 'japones' | 'aleman';
    detallePreu: PreuniversitarioDatos;
    detalleIngles: InglesDatos;
}

export const inicial: Datos = {
    fechaContrato: fechaActual(),
    plan: 'anual',
    fechaInicioClases: fakeDB.fechas.inicioClases.preu,
    fechaTerminoClases: fakeDB.fechas.finClases.preu,
    area: 'preu',
    detallePreu: { ...preuniversitarioInicial },
    detalleIngles: { ...inglesInicial },
};

interface PropsPropios {
    matricula: Datos;
    numeroMatricula?: number;
    onChange(matricula: Datos): void;
    alCambiarFechas(matricula: Datos): void;
    alCambiarNumeroMatricula(numeroMatricula?: number): void;
}

type Props = PropsPropios;

class DatosMatricula extends React.PureComponent<Props> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    private readonly areaOptions = fakeDB.areas.map(item => ({
        key: item.id,
        value: item.id,
        text: item.texto,
    }));

    public render() {
        const {
            matricula,
            onChange,
            numeroMatricula,
            alCambiarFechas,
            alCambiarNumeroMatricula,
        } = this.props;

        const puedeModificarFechaContrato = this.context.usuarioActual.tieneAlgunaDeEstasFunciones(
            'supervisor',
        );

        let componenteDetalle;

        if (matricula.area === 'preu') {
            componenteDetalle = (
                <DatosMatriculaPreu
                    datos={matricula.detallePreu}
                    onChange={detallePreu => {
                        onChange({ ...matricula, detallePreu });
                    }}
                />
            );
        } else {
            componenteDetalle = (
                <DatosMatriculaIngles
                    datos={matricula.detalleIngles}
                    onChange={detalleIngles => {
                        onChange({ ...matricula, detalleIngles });
                    }}
                />
            );
        }

        const planOptions = fakeDB.planesPorArea[this.props.matricula.area].map(item => ({
            key: item.id,
            value: item.id,
            text: item.texto,
        }));

        return (
            <Panel titulo='Datos de matrícula'>
                <Form.Group>
                    <Form.Input
                        readOnly={!puedeModificarFechaContrato}
                        label='Fecha del contrato'
                        type='date'
                        value={matricula.fechaContrato}
                        onChange={(_e, { value }) => {
                            alCambiarFechas({
                                ...matricula,
                                fechaContrato: value || '',
                            });
                        }}
                        width='4'
                    />
                    {this.mostrarNumeroContrato() && (
                        <Form.Input
                            label='Nº de contrato'
                            type='number'
                            value={numeroMatricula || ''}
                            onChange={(_e, { value }) => {
                                const numero =
                                    value && value.length > 0 ? parseInt(value, 10) : undefined;
                                alCambiarNumeroMatricula(numero);
                            }}
                            width='3'
                        />
                    )}
                </Form.Group>

                <Form.Group widths='equal'>
                    <Form.Select
                        label='Área'
                        options={this.areaOptions}
                        value={matricula.area}
                        onChange={(_e, { value }) => {
                            let area = value as Datos['area'];
                            const nuevaMatricula = { ...matricula, area };
                            if (area !== 'ingles' && matricula.plan === 'intensivo-vespertino') {
                                nuevaMatricula.plan = 'intensivo';
                            }
                            onChange(nuevaMatricula);
                            // Alemán e inglés usan los mismos datos que inglés
                            if (area !== 'preu') area = 'ingles';
                            alCambiarFechas({
                                ...nuevaMatricula,
                                fechaInicioClases: fakeDB.fechas.inicioClases[area],
                                fechaTerminoClases: fakeDB.fechas.finClases[area],
                            });
                        }}
                    />
                    <Form.Select
                        label='Plan'
                        options={planOptions}
                        value={matricula.plan}
                        onChange={(_e, { value }) => {
                            onChange({
                                ...matricula,
                                plan: value as never,
                            });
                        }}
                    />
                </Form.Group>

                <Form.Group widths='4'>
                    <Form.Input
                        label='Fecha del inicio de clases'
                        type='date'
                        value={matricula.fechaInicioClases}
                        onChange={(_e, { value }) => {
                            alCambiarFechas({
                                ...matricula,
                                fechaInicioClases: value || '',
                            });
                        }}
                    />
                    <Form.Input
                        label='Fecha del término de clases'
                        type='date'
                        value={matricula.fechaTerminoClases}
                        onChange={(_e, { value }) => {
                            alCambiarFechas({
                                ...matricula,
                                fechaTerminoClases: value || '',
                            });
                        }}
                    />
                </Form.Group>

                <Segment>{componenteDetalle}</Segment>
            </Panel>
        );
    }

    private mostrarNumeroContrato() {
        return false;
    }
}

export default DatosMatricula;



// WEBPACK FOOTER //
// ./src/modulos/Matriculas/IngresoNuevaMatricula/componentes/S3Alumnos/componentes/Alumno/componentes/DatosMatricula/index.tsx